/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { getFormValues } from '@/auth/lib/getFormValues';
import { useCallback, useState, useMemo, useEffect } from 'preact/hooks';
import { getCustomerToken, resendConfirmationEmail } from '@/auth/api';
import { AdditionalActionsAlertProps, useSignInFormProps } from '@/auth/types';
import { DEFAULT__SIGN_IN_EMAIL_FIELD } from '@/auth/configs/defaultCreateUserConfigs';
import { simplifyTransformAttributesForm } from '@/auth/lib/simplifyTransformAttributesForm';
import { checkIsFunction } from '@/auth/lib/checkIsFunction';
import { focusOnEmptyPasswordField } from '@/auth/lib/focusOnEmptyPasswordField';

export const useSignInForm = ({
  emailConfirmationStatusMessage,
  translations,
  initialEmailValue,
  routeSignUp,
  routeForgotPassword,
  routeRedirectOnSignIn,
  onErrorCallback,
  setActiveComponent,
  onSuccessCallback,
  onSignUpLinkClick,
  handleSetInLineAlertProps,
  routeRedirectOnEmailConfirmationClose,
  apiErrorMessageOverride,
}: useSignInFormProps) => {
  const [userEmail, setUserEmail] = useState('');
  const [showEmailConfirmationForm, setShowEmailConfirmationForm] =
    useState(false);
  const [signInPasswordValue, setSignInPasswordValue] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState({
    userName: '',
    status: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [additionalActionsAlert, setAdditionalActionsAlert] =
    useState<AdditionalActionsAlertProps>([]);

  const actionsShowNotificationForm = useCallback(
    async (email: string) => {
      handleSetInLineAlertProps();
      setShowEmailConfirmationForm(true);
      setPasswordError(false);
      setAdditionalActionsAlert([]);

      await resendConfirmationEmail(email);
    },
    [handleSetInLineAlertProps]
  );

  const handleSetPassword = useCallback((value: string) => {
    if (value.length) {
      setPasswordError(false);
    } else {
      setPasswordError(true);
    }
    setSignInPasswordValue(value);
  }, []);

  useEffect(() => {
    if (emailConfirmationStatusMessage?.text) {
      handleSetInLineAlertProps({
        text: emailConfirmationStatusMessage.text,
        type: emailConfirmationStatusMessage.status
          ? emailConfirmationStatusMessage.status
          : undefined,
      });
    }
  }, [emailConfirmationStatusMessage, handleSetInLineAlertProps]);

  const onBlurPassword = useCallback(() => {
    if (!signInPasswordValue.length) {
      setPasswordError(true);
    }
  }, [signInPasswordValue]);

  const checkPasswordAndFocus = useCallback(
    (event: SubmitEvent, isValid: boolean): boolean => {
      if (!signInPasswordValue.length) {
        setPasswordError(true);

        if (!isValid) return true;

        focusOnEmptyPasswordField(event, signInPasswordValue, '');
        return true;
      }

      return false;
    },
    [signInPasswordValue]
  );

  const onAuthenticationSuccess = useCallback(
    (event: SubmitEvent, loginResponse: Record<string, string>) => {
      if (loginResponse?.userName) {
        (event.target as HTMLFormElement).reset();

        if (checkIsFunction(routeRedirectOnSignIn)) {
          window.location.href = routeRedirectOnSignIn();
        } else {
          onSuccessCallback?.({
            userName: loginResponse?.userName,
            status: true,
          });

          setIsSuccessful({
            userName: loginResponse?.userName,
            status: true,
          });
        }
      }
    },
    [onSuccessCallback, routeRedirectOnSignIn]
  );

  const onAuthenticationFailed = useCallback(
    (loginResponse: { errorMessage: string; displayErrorMessage: string }, email: string) => {
      if (loginResponse?.errorMessage?.length) {
        setUserEmail(email);

        /**
         * TODO: Use error code when available, instead of comparing to response text
         *
         * This implementation is a temporary solution.
         * The backend functionality is planned to be improved to obtain accurate data that will help correctly identify error types and handle them.
         */
        // Use original errorMessage for detection (not the override)
        const isIncludesMessage = loginResponse.errorMessage.includes(
          "This account isn't confirmed. Verify and try again."
        );

        // Use displayErrorMessage for display (respects apiErrorMessageOverride)
        const errorMessage: string = isIncludesMessage
          ? translations.resendEmailInformationText
          : loginResponse.displayErrorMessage;

        if (isIncludesMessage) {
          setAdditionalActionsAlert([
            {
              label: translations.resendEmailButtonText,
              onClick: () => {
                actionsShowNotificationForm(email);
              },
            },
          ]);
        } else {
          setAdditionalActionsAlert([]);
        }

        handleSetInLineAlertProps({
          text: errorMessage,
          type: 'error',
        });

        setSignInPasswordValue('');
      }
    },
    [
      actionsShowNotificationForm,
      handleSetInLineAlertProps,
      translations.resendEmailButtonText,
      translations.resendEmailInformationText,
    ]
  );

  const submitLogInUser = useCallback(
    async (event: SubmitEvent, isValid: boolean): Promise<void> => {
      handleSetInLineAlertProps();

      if (checkPasswordAndFocus(event, isValid)) return;

      setIsLoading(true);

      const formValues = getFormValues(event.target);
      const isFormComplete = Object.values(formValues).every((value) => value);

      if (isFormComplete) {
        const { email, password } = formValues;

        const loginResponse = await getCustomerToken({
          email,
          password,
          handleSetInLineAlertProps,
          onErrorCallback,
          translations,
          apiErrorMessageOverride,
        });

        onAuthenticationFailed(loginResponse, email);
        onAuthenticationSuccess(event, loginResponse);

        setPasswordError(false);
      }

      setIsLoading(false);
    },
    [
      translations,
      onErrorCallback,
      checkPasswordAndFocus,
      onAuthenticationFailed,
      onAuthenticationSuccess,
      handleSetInLineAlertProps,
      apiErrorMessageOverride,
    ]
  );

  const forgotPasswordCallback = useCallback(() => {
    if (checkIsFunction(setActiveComponent)) {
      setActiveComponent('resetPasswordForm');

      return;
    }

    if (checkIsFunction(routeForgotPassword)) {
      window.location.href = routeForgotPassword();
    }
  }, [routeForgotPassword, setActiveComponent]);

  const onSignUpLinkClickCallback = useCallback(() => {
    if (checkIsFunction(onSignUpLinkClick)) {
      onSignUpLinkClick();
    }

    if (checkIsFunction(setActiveComponent)) {
      setActiveComponent('signUpForm');

      return;
    }

    if (checkIsFunction(routeSignUp)) {
      window.location.href = routeSignUp();
    }
  }, [onSignUpLinkClick, routeSignUp, setActiveComponent]);

  const defaultEnhancedEmailFields: any = useMemo(() => {
    const fieldsList = simplifyTransformAttributesForm(
      DEFAULT__SIGN_IN_EMAIL_FIELD
    );

    if (!initialEmailValue?.length) return fieldsList;

    return fieldsList?.map((el: any) => ({
      ...el,
      defaultValue: initialEmailValue,
    }));
  }, [initialEmailValue]);

  const handledOnPrimaryButtonClick = useCallback(() => {
    handleSetInLineAlertProps();

    if (checkIsFunction(routeRedirectOnEmailConfirmationClose)) {
      window.location.href = routeRedirectOnEmailConfirmationClose();
    } else {
      setShowEmailConfirmationForm(false);
    }
  }, [handleSetInLineAlertProps, routeRedirectOnEmailConfirmationClose]);

  return {
    additionalActionsAlert,
    userEmail,
    defaultEnhancedEmailFields,
    passwordError,
    isSuccessful,
    isLoading,
    signInPasswordValue,
    showEmailConfirmationForm,
    setShowEmailConfirmationForm,
    setSignInPasswordValue,
    submitLogInUser,
    forgotPasswordCallback,
    onSignUpLinkClickCallback,
    handledOnPrimaryButtonClick,
    handleSetPassword,
    onBlurPassword,
  };
};
